import jsPDF from 'jspdf';
import 'jspdf-autotable';

const formatMoney = (value) => {
  const amount = Number(value) || 0;
  return amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toISOString().split('T')[0];
};

/**
 * Shared invoice PDF for builder, list, and guest landing.
 * Body watermark is disabled (founder: no SAMPLE overlay).
 */
export async function generateInvoicePdf(invoice, options = {}) {
  const {
    customBranding = false,
    apiUrl = '',
    download = true,
  } = options;

  const doc = new jsPDF();
  const pageHeight = doc.internal.pageSize.height;
  const pageWidth = doc.internal.pageSize.width;
  const billFrom = invoice.billFrom || {};
  const client = invoice.client_info || {};
  const items = invoice.items || [];
  const subtotal = invoice.subtotal ?? items.reduce((sum, item) => sum + (item.total || 0), 0);
  const taxAmount = invoice.tax?.amount ?? 0;
  const total = invoice.total ?? 0;
  const balanceDue = invoice.balanceDue ?? total;
  const filename = `invoice-${invoice.invoiceNumber || Date.now()}.pdf`;

  const addFooter = () => {
    const footerHeight = 15;
    const footerY = pageHeight - footerHeight;
    doc.setFillColor(240, 240, 240);
    doc.rect(0, footerY, pageWidth, footerHeight, 'F');
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.5);
    doc.line(0, footerY, pageWidth, footerY);

    const preparedBy = customBranding
      ? `Prepared by ${billFrom.company || billFrom.name || 'Invoice'}`
      : 'Prepared by Ainix Invoice';
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.setFont(undefined, 'normal');
    doc.text(preparedBy, pageWidth / 2, footerY + 8, { align: 'center' });
  };

  doc.setFillColor(245, 245, 245);
  doc.rect(0, 0, pageWidth, 50, 'F');
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(1);
  doc.line(0, 50, pageWidth, 50);

  if (billFrom.logo && customBranding && apiUrl) {
    try {
      const filenameLogo = billFrom.logo.split('/').pop();
      const response = await fetch(`${apiUrl}/upload/logo/${filenameLogo}`);
      if (response.ok) {
        const blob = await response.blob();
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
        doc.addImage(base64, 'PNG', 10, 8, 25, 15);
      }
    } catch {
      // Continue without logo
    }
  }

  doc.setFontSize(22);
  doc.setTextColor(60, 60, 60);
  doc.setFont(undefined, 'bold');
  doc.text('INVOICE', pageWidth - 20, 18, { align: 'right' });
  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(80, 80, 80);
  doc.text(`Invoice #: ${invoice.invoiceNumber || 'INV'}`, pageWidth - 20, 28, { align: 'right' });
  doc.text(`Date: ${formatDate(invoice.issueDate) || formatDate(new Date())}`, pageWidth - 20, 34, {
    align: 'right',
  });
  if (invoice.dueDate) {
    doc.text(`Due: ${formatDate(invoice.dueDate)}`, pageWidth - 20, 40, { align: 'right' });
  }

  let y = 62;
  doc.setFontSize(11);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(40, 40, 40);
  doc.text('FROM', 20, y);
  doc.text('BILL TO', 110, y);
  y += 6;
  doc.setFont(undefined, 'normal');
  doc.setFontSize(10);
  const fromLines = [billFrom.name, billFrom.company, billFrom.email, billFrom.phone, billFrom.address].filter(Boolean);
  const toLines = [client.name, client.company, client.email, client.phone, client.address].filter(Boolean);
  const rowCount = Math.max(fromLines.length, toLines.length, 1);
  for (let i = 0; i < rowCount; i += 1) {
    if (fromLines[i]) doc.text(String(fromLines[i]), 20, y + i * 5);
    if (toLines[i]) doc.text(String(toLines[i]), 110, y + i * 5);
  }
  y += rowCount * 5 + 10;

  const tableBody = items.map((item) => [
    item.description || '',
    String(item.quantity ?? 0),
    formatMoney(item.price),
    formatMoney(item.total ?? (item.quantity || 0) * (item.price || 0)),
  ]);

  doc.autoTable({
    startY: y,
    head: [['Description', 'Qty', 'Price', 'Total']],
    body: tableBody.length ? tableBody : [['—', '0', '0.00', '0.00']],
    theme: 'striped',
    headStyles: { fillColor: [55, 65, 81] },
    styles: { fontSize: 9 },
  });

  y = (doc.lastAutoTable?.finalY || y) + 10;
  if (y > pageHeight - 50) {
    addFooter();
    doc.addPage();
    y = 20;
  }

  const totals = [
    ['Subtotal', formatMoney(subtotal)],
    invoice.tax?.rate ? [`Tax (${invoice.tax.rate}%)`, formatMoney(taxAmount)] : null,
    invoice.discount ? ['Discount', `-${formatMoney(invoice.discount)}`] : null,
    invoice.shipping ? ['Shipping', formatMoney(invoice.shipping)] : null,
    ['Total', formatMoney(total)],
    invoice.amountPaid ? ['Amount paid', formatMoney(invoice.amountPaid)] : null,
    ['Balance due', formatMoney(balanceDue)],
  ].filter(Boolean);

  totals.forEach(([label, value], index) => {
    const isEmphasis = label === 'Total' || label === 'Balance due';
    doc.setFont(undefined, isEmphasis ? 'bold' : 'normal');
    doc.setFontSize(isEmphasis ? 11 : 10);
    doc.text(label, pageWidth - 80, y + index * 6);
    doc.text(value, pageWidth - 20, y + index * 6, { align: 'right' });
  });

  y += totals.length * 6 + 12;
  const addWrapped = (title, text) => {
    if (!text) return;
    doc.setFont(undefined, 'bold');
    doc.setFontSize(10);
    doc.text(title, 20, y);
    y += 6;
    doc.setFont(undefined, 'normal');
    const lines = doc.splitTextToSize(String(text), pageWidth - 40);
    doc.text(lines, 20, y);
    y += lines.length * 5 + 6;
  };
  addWrapped('NOTES', invoice.notes);
  addWrapped('TERMS & CONDITIONS', invoice.terms);

  addFooter();

  const base64 = doc.output('datauristring').split(',')[1] || '';
  if (download) {
    doc.save(filename);
  }

  return { filename, base64 };
}

export function shouldWatermark() {
  return false;
}

export function markPdfDownloaded(userId) {
  if (!userId) return;
  localStorage.setItem(`ainix_downloaded_pdf_${userId}`, '1');
}
